import { Link } from "@/lib/nav";
import type { ReactNode } from "react";

import EmptyState from "@buildpy/ui/empty-state";
import ListRowCard, { ListRows } from "@/components/list-card";
import Money from "@/components/money";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@buildpy/ui/table";
import {
  EMPTY_MARK,
  formatDate,
  formatDateTime,
  formatWon,
} from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import { NEW_SALES_SITE, newSalesSummaryOf } from "@buildpy/core/modules/customer/journey";
import { customerSubtitle } from "@buildpy/core/modules/customer/journey-panel";
import { activitySummaryText } from "@buildpy/core/modules/customer/stage-activity";
import { STAGE_CAUSE_LABEL, stageButtons, stageEventActorText, revertTarget } from "@buildpy/core/modules/customer/stage-events";
import {
  BUDGET_BAND_LABEL,
  CALL_TIME_LABEL,
  CUSTOMER_SOURCE_LABEL,
  CUSTOMER_STAGE_LABEL,
  type Customer,
  WORK_SCOPE_LABEL,
} from "@buildpy/core/modules/customer/types";
import CustomerEditSheet from "@/modules/customer/ui/customer-edit-sheet";
import { CustomerSites, CustomerTodos, JourneyStepper, LinkedDocs } from "@/modules/customer/ui/customer-journey";
import QuoteStatusBadge from "@/modules/quote/ui/status-badge";
import StageMenu from "@/modules/customer/ui/stage-menu";
import StartQuoteButton from "@/modules/customer/ui/start-quote-button";
import ReferralLinkCard from "@/modules/referral/ui/referral-link-card";
import { resolveTradeName } from "@buildpy/core/modules/template/trades";
import type { CustomerView } from "@buildpy/core/views/g1a/customer";
import { CrumbName } from "@/shell/crumb";
import PageHeader from "@/shell/page-header";

// 고객 상세 — 상담 한 건을 여는 화면이자 견적으로 넘어가는 문이다(카드 넷: 정보 · 상담 체크 · 견적 · 이력).
const Page = ({ data }: { data: CustomerView }) => {
  const {
    customer,
    members,
    quotes,
    activities,
    tradeRows,
    stageEvents,
    stageEventsFailed,
    stageFacts,
    projectCount,
    freshCtx,
    pickKind,
    selected,
    headStage,
    ongoing,
    steps,
    todos,
    docs,
    coNote,
    sourceProject,
    sourcePage,
    sourcePortfolio,
    referrer,
    referredCount,
    referralLink,
    referralEligible,
    referralUrl,
    firstContact,
    consultTrades,
    sites,
    formCodes,
    labels,
    lostReasons,
    stageReasons,
  } = data;
  const memberName = new Map(members.map((row) => [row.id, row.name]));
  const consult = customer.consult;

  return (
    <>
      <CrumbName name={customer.name} />
      <PageHeader
        actions={
          <>
            <Button asChild size="sm" variant="outline">
              <Link href="/app/customers">목록</Link>
            </Button>
            {/* 가견적을 먼저 둔다 — 흐름상 상세견적보다 앞이다(제품 원칙(옛 5절)). */}
            <StartQuoteButton customerId={customer.id} rough />
            <StartQuoteButton customerId={customer.id} />
            {ongoing && (
              <Button asChild size="sm">
                <Link href={`/app/projects/${ongoing.id}`}>진행 중 현장으로</Link>
              </Button>
            )}
          </>
        }
        description={[
          customerSubtitle({
            projectCount,
            assigneeName: customer.assignee_id ? (memberName.get(customer.assignee_id) ?? null) : null,
            created_at: customer.created_at,
            sourceLabel: customer.source ? CUSTOMER_SOURCE_LABEL[customer.source] : null,
          }),
          // 재문의(headStage)면 지난 이탈의 사유는 머리에 두지 않는다 — 단계 이력엔 남는다.
          customer.stage === "LOST" && customer.lost_reason && !headStage
            ? `이탈 사유 — ${labels.lost_reason}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")}
        meta={
          <StageMenu
            buttons={stageFacts ? stageButtons(stageFacts) : { contact: false, lost: false, revert: false }}
            customerId={customer.id}
            display={headStage}
            lostReasons={lostReasons}
            revertTo={stageFacts && !headStage ? revertTarget(stageFacts) : null}
            stage={customer.stage}
          />
        }
        title={customer.name}
      />

      <div className="mb-4 flex flex-col gap-y-4">
        {sites.length > 0 && (
          <CustomerSites
            customerId={customer.id}
            newSales={freshCtx ? newSalesSummaryOf(freshCtx) : null}
            selectedId={pickKind === "new" ? NEW_SALES_SITE : (selected?.id ?? null)}
            sites={sites}
          />
        )}
        <JourneyStepper note={coNote} site={pickKind === "new" ? "새 영업" : (selected?.title ?? null)} steps={steps} />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <CustomerTodos className={docs.length ? "lg:col-span-3" : "lg:col-span-5"} customerId={customer.id} todos={todos} />
          {docs.length > 0 && <LinkedDocs className="lg:col-span-2" docs={docs} />}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-y-4 lg:col-span-2">
          <Card
            action={
              <CustomerEditSheet
                codes={formCodes}
                customer={customer}
                members={members}
                sections={["info"]}
                trades={consultTrades}
                title="고객 정보 편집"
              />
            }
            title="고객 정보">
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
              <Row label="이름" value={customer.name} />
              <Row label="휴대폰" value={customer.phone} />
              <Row
                label="현장 주소"
                value={
                  customer.site_address && (
                    <>
                      {customer.site_address}
                      {customer.address_zonecode && <span className="ml-1 text-muted-foreground">({customer.address_zonecode})</span>}
                    </>
                  )
                }
              />
              <Row
                label="거주형태"
                value={labels.residence}
              />
              <Row
                label="평형대"
                value={labels.area_band}
              />
              <Row
                label="현재 공간 상태"
                value={labels.space_state}
              />
              <Row
                label="확장 필요"
                value={needExtensionLabel(customer.need_extension)}
              />
              <Row
                label="예상 입주일"
                value={formatDate(customer.move_in_hope, "")}
              />
              <Row
                label="공사 예정일"
                value={formatDate(customer.work_start_hope, "")}
              />
              <Row
                label="실측 가능일"
                value={formatDate(customer.survey_date, "")}
              />
              <Row label="예산" value={formatWon(customer.budget, "")} />
              <Row label="공사 종류" value={customer.work_scope ? WORK_SCOPE_LABEL[customer.work_scope] : ""} />
              <Row label="예산대" value={customer.budget_band ? BUDGET_BAND_LABEL[customer.budget_band] : ""} />
              <Row label="통화 가능 시간" value={customer.call_time ? CALL_TIME_LABEL[customer.call_time] : ""} />
              <Row
                label="유입"
                value={
                  sourceProject ? (
                    <span data-testid="customer-source-notice">
                      {CUSTOMER_SOURCE_LABEL.NOTICE} ·{" "}
                      <Link className="underline underline-offset-2" href={`/app/projects/${sourceProject.id}`}>
                        {sourceProject.title}
                      </Link>
                    </span>
                  ) : customer.source === "PAGE" ? (
                    <span data-testid="customer-source-page">
                      {CUSTOMER_SOURCE_LABEL.PAGE} · {sourcePage ? sourcePage.name : "기본 목록"}
                      {sourcePortfolio && (
                        <>
                          {" · "}
                          <Link className="underline underline-offset-2" href={`/app/portfolio/${sourcePortfolio.id}`}>
                            {sourcePortfolio.title}
                          </Link>
                        </>
                      )}
                    </span>
                  ) : customer.source === "REFERRAL" && referrer ? (
                    <span data-testid="customer-source-referral">
                      {CUSTOMER_SOURCE_LABEL.REFERRAL} ·{" "}
                      <Link className="underline underline-offset-2" href={`/app/customers/${referrer.id}`}>
                        {referrer.name}
                      </Link>
                    </span>
                  ) : customer.source ? (
                    CUSTOMER_SOURCE_LABEL[customer.source]
                  ) : (
                    ""
                  )
                }
              />
              <Row
                label="담당자"
                value={
                  customer.assignee_id
                    ? (memberName.get(customer.assignee_id) ?? "")
                    : ""
                }
              />
              <Row label="등록일" value={formatDate(customer.created_at, "")} />
              {firstContact && <Row label="첫 응대" value={firstContact} />}
              {referredCount > 0 && (
                <Row label="소개로 들어온 문의" value={<span data-testid="customer-referred-count">{referredCount}건</span>} />
              )}
              <div className="md:col-span-2">
                <Row label="메모" value={customer.memo} />
              </div>
            </dl>
          </Card>

          <Card
            action={
              <CustomerEditSheet
                codes={formCodes}
                customer={customer}
                members={members}
                sections={["consult"]}
                trades={consultTrades}
                title="공종별 상담 체크 편집"
              />
            }
            meta={`${consult.length}개 공종`}
            title="상담 체크">
            {consult.length === 0 ? (
              <EmptyState variant="plain">체크한 공종이 없습니다.</EmptyState>
            ) : (
              <ul className="flex flex-col divide-y divide-border">
                {consult.map((need) => (
                  <li
                    key={need.trade}
                    className="grid grid-cols-1 gap-x-4 gap-y-0.5 py-2 md:grid-cols-[9rem_1fr]">
                    <span className="text-sm font-medium">
                      {resolveTradeName(tradeRows, need.trade)}
                    </span>
                    <div className="flex flex-col gap-y-0.5">
                      <span className="text-sm text-muted-foreground">
                        {need.detail || EMPTY_MARK}
                      </span>
                      {need.note && (
                        <span className="text-xs text-muted-foreground">
                          특이사항 — {need.note}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card meta={`${quotes.length}건`} title="견적">
            {quotes.length === 0 ? (
              <EmptyState variant="plain">
                아직 견적이 없습니다. 위의 「견적 작성」 으로 초안을 만듭니다.
              </EmptyState>
            ) : (
              <>
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="h-9 px-2">번호</TableHead>
                        <TableHead className="h-9 px-2">버전</TableHead>
                        <TableHead className="h-9 px-2">상태</TableHead>
                        <TableHead className="h-9 px-2 text-right">
                          최종금액
                        </TableHead>
                        <TableHead className="h-9 px-2 text-right">발송일</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quotes.map((quote) => (
                        <TableRow key={quote.id}>
                          <TableCell className="px-2 py-2 font-medium">
                            <Link
                              className="hover:underline"
                              href={`/app/quotes/${quote.id}`}>
                              {quote.quote_no}
                            </Link>
                            {/*
                              가견적과 상세견적이 같은 표에 나란히 서므로, 표시가
                              없으면 「5,175만 · 6,000만」 이 같은 성격의 금액으로
                              읽힌다. 하나는 개산이고 하나는 도급금액이다.
                            */}
                            {quote.kind === "ROUGH" && (
                              <span className="ml-1.5 rounded-md bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                                가견적
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="px-2 py-2 tabular-nums text-muted-foreground">
                            v{quote.version}
                          </TableCell>
                          <TableCell className="px-2 py-2">
                            <QuoteStatusBadge status={quote.status} />
                          </TableCell>
                          <TableCell className="px-2 py-2 text-right tabular-nums">
                            <Money value={quote.total} />
                          </TableCell>
                          <TableCell className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-muted-foreground">
                            {formatDate(quote.sent_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* 폰(md 미만)은 한 행 = 한 줄 카드 — 표는 금액·발송일 열이 밀렸다. */}
                <ListRows className="md:hidden" label="견적 목록" testId="customer-quote-rows">
                  {quotes.map((quote) => (
                    <ListRowCard
                      key={quote.id}
                      amount={<Money value={quote.total} />}
                      badge={<QuoteStatusBadge status={quote.status} />}
                      href={`/app/quotes/${quote.id}`}
                      meta={[quote.sent_at ? `발송 ${formatDate(quote.sent_at)}` : "미발송"]}
                      subtitle={quote.title}
                      tag={quote.kind === "ROUGH" && <Pill>가견적</Pill>}
                      testId={`customer-quote-row-${quote.id}`}
                      title={`${quote.quote_no} v${quote.version}`}
                      variant="row"
                    />
                  ))}
                </ListRows>
              </>
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-y-4 lg:col-span-1">
          {(referralEligible || referralLink) && (
            <ReferralLinkCard
              customerId={customer.id}
              link={referralLink && referralUrl ? { id: referralLink.id, active: referralLink.active, url: referralUrl } : null}
              referred={referredCount}
            />
          )}
          <Card meta={stageEventsFailed ? undefined : `${stageEvents.length}건`} title="단계 이력">
            {stageEventsFailed ? (
              <EmptyState variant="plain">
                <span data-testid="customer-stage-history-failed">단계 이력을 불러오지 못했습니다. 잠시 뒤 다시 열어 주세요.</span>
              </EmptyState>
            ) : stageEvents.length === 0 ? (
              <EmptyState variant="plain">단계가 바뀐 적이 없습니다.</EmptyState>
            ) : (
              <ul className="flex flex-col gap-y-3" data-testid="customer-stage-history">
                {[...stageEvents].reverse().map((event) => {
                  const { lost: lostLabel, reason: reasonText } = stageReasons[event.id] ?? { lost: null, reason: null };
                  return (
                    <li key={event.id} className="flex flex-col gap-y-0.5">
                      <span className="text-sm">
                        {CUSTOMER_STAGE_LABEL[event.from_stage]} → {CUSTOMER_STAGE_LABEL[event.to_stage]}
                        <span className="ml-1.5 text-xs text-muted-foreground">{STAGE_CAUSE_LABEL[event.cause]}</span>
                      </span>
                      {lostLabel && (
                        <span className="text-xs text-muted-foreground" data-testid="customer-stage-lost-code">
                          이탈 사유 — {lostLabel}
                        </span>
                      )}
                      {reasonText && <span className="text-xs text-muted-foreground">사유 — {reasonText}</span>}
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(event.created_at)} · {stageEventActorText(event, event.actor_id ? memberName.get(event.actor_id) : null)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
          <Card title="이력">
            {activities.length === 0 ? (
              <EmptyState variant="plain">기록된 이력이 없습니다.</EmptyState>
            ) : (
              <ul className="flex flex-col gap-y-3">
                {activities.map((activity) => (
                  <li key={activity.id} className="flex flex-col gap-y-0.5">
                    <span className="text-sm">{activitySummaryText(activity)}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(activity.created_at)}
                      {activity.actor_id
                        ? ` · ${memberName.get(activity.actor_id) ?? activity.actor_id}`
                        : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

export default Page;

/* ── 조각들 ────────────────────────────────────────────────── */

const needExtensionLabel = (value: Customer["need_extension"]): string => {
  if (value === true) return "필요";
  if (value === false) return "없음";
  return "";
};

const Card = ({
  title,
  meta,
  action,
  className,
  children,
}: {
  title: string;
  meta?: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <SectionCard className={cn("flex flex-col gap-y-3", className)}>
    <div className="flex items-center justify-between gap-x-2">
      <div className="flex items-baseline gap-x-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
      </div>
      {action}
    </div>
    {children}
  </SectionCard>
);

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex flex-col gap-y-0.5">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="whitespace-pre-wrap text-sm">{value || EMPTY_MARK}</dd>
  </div>
);
